import { RefreshCw } from "lucide-react"
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const loadingStateVariants = cva(
  'flex flex-col items-center justify-center gap-3 text-sm text-gray-500',
  {
    variants: {
      variant: {
        card: 'min-h-[180px] rounded-lg border bg-cfb-light p-6',
        inline: 'min-h-[140px] p-6',
        compact: 'min-h-[100px] p-4',
        centered: 'min-h-[280px] p-6',
        page: 'min-h-screen p-6',
      },
    },
    defaultVariants: {
      variant: 'inline',
    },
  }
)

const spinnerSizeByVariant: Record<string, string> = {
  card: 'size-7',
  inline: 'size-6',
  compact: 'size-5',
  centered: 'size-7',
  page: 'size-10',
}

export function LoadingSpinner({ className }: { className?: string }) {
  return (
    <RefreshCw
      className={cn('shrink-0 animate-spin text-brand', className)}
      aria-hidden
    />
  )
}

export type LoadingStateProps = VariantProps<typeof loadingStateVariants> & {
  message?: string
  className?: string
  showSpinner?: boolean
}

export function LoadingState({
  message = 'Loading...',
  variant,
  className,
  showSpinner = true,
}: LoadingStateProps) {
  const spinnerSize = spinnerSizeByVariant[variant ?? 'inline'] ?? 'size-6'

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn(loadingStateVariants({ variant }), className)}
    >
      {showSpinner && <LoadingSpinner className={spinnerSize} />}
      <span>{message}</span>
    </div>
  )
}
